import { Fragment, useEffect, useId, useRef, useState } from "react"
import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  PopoverBody,
  PopoverContent,
  PopoverPositioner,
  PopoverRoot,
  PopoverTrigger,
  RadioGroup,
  Text,
  VisuallyHidden,
  VStack,
  chakra,
} from "@chakra-ui/react"
import { DateTime, utc } from "timezonecomplete"
import { FaCheck, FaChevronDown, FaRegCalendar } from "react-icons/fa6"
import { SegmentedControl } from "@hydrologiq/shared/chakra/SegmentedControl"
import {
  TIMESCALE_PERIOD_PRESETS,
  customDraftFromRange,
  formatBrowserDateTime,
  normaliseCustomRange,
  normaliseSelection,
  resolveEntityBounds,
  type AppliedRange,
  type CustomDraft,
  type EntityBoundarySource,
  type NormaliseError,
  type NormaliseResult,
  type StagedSelection,
  type TimeRangeMode,
  type TimescalePeriodValue,
} from "./TimeRange"

/** Relative preset staged when a Custom range is applied and the user switches back to Relative. */
const DEFAULT_RELATIVE_PRESET: TimescalePeriodValue = "7d"

const MS_MINUTE = 60 * 1000

/**
 * Preset columns, left to right. Reading order (and arrow-key order) stays the `TIMESCALE_PERIOD_PRESETS` order:
 * the left column is every range within a day, the right column every range over a day.
 * Last 6 months and Last 12 months are still valid presets (a saved one is applied and shown in the trigger) but
 * are not listed, so opening the popup with one of them applied selects no row.
 */
const PRESET_COLUMNS: { id: string; values: readonly TimescalePeriodValue[] }[] = [
  { id: "within-a-day", values: ["1h", "3h", "6h", "12h", "24h"] },
  { id: "over-a-day", values: ["3d", "7d", "30d", "90d", "all"] },
]

/** Selected Mode_Switch label and underline colour, scoped to this control. */
const MODE_SWITCH_SELECTED = "#6d44b5"

/**
 * Minimum height of the panel under the tabs, shared by Relative and Custom. It is the Custom panel's natural height
 * (two labelled inputs with reserved error space, and the status line) plus a little slack. Check it in a browser
 * if the inputs, labels or error text change size.
 */
const PANEL_MIN_HEIGHT = "15rem"

/** Background of the selected preset row. */
const SELECTED_ROW_BACKGROUND = "#f1ebfa"

/** Real clock, used only when the caller does not inject `now`. It is read in event handlers, and in render for the trigger summary's year. */
const systemNow = (): DateTime => DateTime.nowUtc()

/** `YYYY-MM-DD HH:mm` in `browserTimeZone`, for display. */
const displayDateTime = (valueUtc: DateTime, browserTimeZone: string): string =>
  formatBrowserDateTime(valueUtc, browserTimeZone).replace("T", " ")

const displayRange = (startUtc: DateTime, endUtc: DateTime, browserTimeZone: string): string =>
  `${displayDateTime(startUtc, browserTimeZone)} – ${displayDateTime(endUtc, browserTimeZone)}`

/**
 * A short form of a range for the one-line trigger: the year is dropped when both ends are in the current year, and
 * the end date is dropped when the range starts and ends on the same day. The full range is still the description.
 */
function compactRange(startUtc: DateTime, endUtc: DateTime, browserTimeZone: string, now: DateTime): string {
  const start = displayDateTime(startUtc, browserTimeZone)
  const end = displayDateTime(endUtc, browserTimeZone)
  const thisYear = displayDateTime(now, browserTimeZone).slice(0, 4)
  const dropYear = start.slice(0, 4) === thisYear && end.slice(0, 4) === thisYear
  const short = (value: string) => (dropYear ? value.slice(5) : value)
  const endText = start.slice(0, 10) === end.slice(0, 10) ? end.slice(11) : short(end)
  return `${short(start)} – ${endText}`
}

/**
 * Trigger summary. `title` is the visible label: the preset label for a Relative range, a compact form of the range
 * for a Custom one. `badge` names the mode. `description` is the full text read out as the trigger's description.
 */
function summarise(
  applied: AppliedRange,
  browserTimeZone: string,
  now: DateTime
): { title: string; badge: string; description: string } {
  const range = displayRange(applied.startUtc, applied.endUtc, browserTimeZone)
  if (applied.mode === "custom") {
    return {
      title: compactRange(applied.startUtc, applied.endUtc, browserTimeZone, now),
      badge: "Custom",
      description: `Custom: ${range}`,
    }
  }
  const label = TIMESCALE_PERIOD_PRESETS.find((preset) => preset.value === applied.preset)?.label ?? "Relative"
  return { title: label, badge: "Relative", description: `${label}: ${range}` }
}

/**
 * Native `min` and `max` for the Custom inputs, as browser-local minute values.
 * The lower bound rounds up and the upper bound rounds down, matching the normaliser.
 */
function inputBounds(
  source: EntityBoundarySource,
  now: DateTime,
  browserTimeZone: string
): { min?: string; max: string } {
  const { earliestAllowedStart, latestAllowedEnd } = resolveEntityBounds(source, now)
  return {
    min: earliestAllowedStart
      ? formatBrowserDateTime(
          new DateTime(Math.ceil(earliestAllowedStart.unixUtcMillis() / MS_MINUTE) * MS_MINUTE, utc()),
          browserTimeZone
        )
      : undefined,
    max: formatBrowserDateTime(latestAllowedEnd, browserTimeZone),
  }
}

interface FieldErrors {
  from?: string
  to?: string
  /** Errors that belong to neither input, such as having no whole-minute range available. */
  form?: string
}

function toFieldErrors(errors: NormaliseError[]): FieldErrors {
  const fieldErrors: FieldErrors = {}
  for (const error of errors) {
    if (error.field === "from") fieldErrors.from = error.message
    else if (error.field === "to") fieldErrors.to = error.message
    else fieldErrors.form = error.message
  }
  return fieldErrors
}

export interface TimeRangeControlProps {
  /** Canonical applied range. It seeds the staged selection each time the popup opens. */
  applied: AppliedRange
  /** Asset assignment or System installation bounds, used for the input limits and blur correction. */
  boundarySource: EntityBoundarySource
  /** Browser IANA timezone, supplied by the caller so the control reads no browser globals. */
  browserTimeZone: string
  /** Commit a staged selection, normally `useTimeRangeController().apply`. A result with errors is not committed. */
  onApply: (selection: StagedSelection) => NormaliseResult
  /** Called with true when the popup opens and with false when it closes (Apply, Cancel, Escape, an outside click or unmount). */
  onOpenChange?: (open: boolean) => void
  /** Injectable clock. Defaults to the real clock. */
  now?: () => DateTime
  /** Disables the trigger. */
  disabled?: boolean
}

/**
 * Time range control for Timescale monitoring views.
 *
 * Edits inside the popup are staged: nothing reaches `onApply` until Apply, and
 * Cancel, Escape or an outside click discard them (the next open is seeded from
 * `applied` again). The control owns no GraphQL requests, preference writes or
 * graph granularity.
 */
export function TimeRangeControl({
  applied,
  boundarySource,
  browserTimeZone,
  onApply,
  onOpenChange,
  now = systemNow,
  disabled = false,
}: TimeRangeControlProps) {
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<TimeRangeMode>("relative")
  const [preset, setPreset] = useState<TimescalePeriodValue>(DEFAULT_RELATIVE_PRESET)
  const [draft, setDraft] = useState<CustomDraft>({ from: "", to: "" })
  const [limits, setLimits] = useState<{ min?: string; max: string }>()
  const [errors, setErrors] = useState<FieldErrors>({})
  const [correction, setCorrection] = useState("")
  // Announces an Apply-time correction after the popup (and its status region) has closed.
  const [appliedCorrection, setAppliedCorrection] = useState("")

  const onOpenChangeRef = useRef(onOpenChange)
  useEffect(() => {
    onOpenChangeRef.current = onOpenChange
  })
  useEffect(() => {
    if (!open) return
    onOpenChangeRef.current?.(true)
    return () => onOpenChangeRef.current?.(false)
  }, [open])

  const contentRef = useRef<HTMLDivElement>(null)
  const fromRef = useRef<HTMLInputElement>(null)
  const toRef = useRef<HTMLInputElement>(null)

  const id = useId()
  const summaryId = `${id}-summary`
  const fromId = `${id}-from`
  const toId = `${id}-to`
  const fromErrorId = `${id}-from-error`
  const toErrorId = `${id}-to-error`
  const formErrorId = `${id}-form-error`
  const statusId = `${id}-status`

  const summary = summarise(applied, browserTimeZone, now())

  const seedFromApplied = () => {
    const current = now()
    setMode(applied.mode)
    setPreset(applied.mode === "relative" && applied.preset ? applied.preset : DEFAULT_RELATIVE_PRESET)
    // Custom starts from the applied range, so switching from Relative continues from what is on screen.
    setDraft(
      applied.mode === "custom"
        ? {
            from: formatBrowserDateTime(applied.startUtc, browserTimeZone),
            to: formatBrowserDateTime(applied.endUtc, browserTimeZone),
          }
        : customDraftFromRange(applied.startUtc, applied.endUtc, boundarySource, current, browserTimeZone)
    )
    setLimits(inputBounds(boundarySource, current, browserTimeZone))
    setErrors({})
    setCorrection("")
    setAppliedCorrection("")
  }

  const handleOpenChange = ({ open: nextOpen }: { open: boolean }) => {
    if (nextOpen) seedFromApplied()
    setOpen(nextOpen)
  }

  const handleModeChange = (nextMode: TimeRangeMode) => {
    setMode(nextMode)
    setErrors({})
    setCorrection("")
  }

  /** Selecting a preset also moves the Custom inputs to that preset's range, so switching to Custom continues from it. */
  const handlePresetChange = (nextPreset: TimescalePeriodValue) => {
    setPreset(nextPreset)
    const current = now()
    const result = normaliseSelection({ mode: "relative", preset: nextPreset }, boundarySource, current, browserTimeZone)
    if (result.applied) {
      setDraft(customDraftFromRange(result.applied.startUtc, result.applied.endUtc, boundarySource, current, browserTimeZone))
    }
    setErrors({})
    setCorrection("")
  }

  const handleDraftChange = (field: keyof CustomDraft, value: string) => {
    setDraft((previous) => ({ ...previous, [field]: value }))
    setErrors((previous) => ({ ...previous, [field]: undefined, form: undefined }))
    setCorrection("")
  }

  /** Canonicalise parseable values on blur. Incomplete or unparsable values stay invalid; none are invented. */
  const handleBlur = () => {
    const result = normaliseCustomRange(draft, boundarySource, now(), browserTimeZone)

    if (result.errors.length > 0) {
      setErrors(toFieldErrors(result.errors))
      setCorrection("")
      return
    }

    setErrors({})
    if (result.corrected && result.canonicalDraft) {
      setDraft(result.canonicalDraft)
      setCorrection(
        `Adjusted to ${result.canonicalDraft.from.replace("T", " ")} – ${result.canonicalDraft.to.replace("T", " ")} ` +
          "to fit the available data and the minimum range length."
      )
    } else {
      setCorrection("")
    }
  }

  /** Show errors, keep the popup open and move focus to the first invalid input. */
  const showApplyErrors = (applyErrors: NormaliseError[]) => {
    const fieldErrors = toFieldErrors(applyErrors)
    setErrors(fieldErrors)
    ;(fieldErrors.from ? fromRef : fieldErrors.to ? toRef : undefined)?.current?.focus()
  }

  const handleApply = () => {
    // An incomplete or unparsable Custom draft is rejected here, so `onApply` only sees parseable values.
    if (mode === "custom") {
      const check = normaliseCustomRange(draft, boundarySource, now(), browserTimeZone)
      if (check.errors.length > 0) return showApplyErrors(check.errors)
    }

    const result = onApply(mode === "relative" ? { mode, preset } : { mode, draft })
    if (result.errors.length > 0) return showApplyErrors(result.errors)

    setAppliedCorrection(
      result.corrected && result.applied
        ? `Time range adjusted to ${displayRange(result.applied.startUtc, result.applied.endUtc, browserTimeZone)}.`
        : ""
    )
    setOpen(false)
  }

  const handleCancel = () => setOpen(false)

  /** True when the staged selection differs from the applied range, so Apply has something to apply. */
  const hasChanges =
    mode === "relative"
      ? applied.mode !== "relative" || applied.preset !== preset
      : applied.mode !== "custom" ||
        draft.from !== formatBrowserDateTime(applied.startUtc, browserTimeZone) ||
        draft.to !== formatBrowserDateTime(applied.endUtc, browserTimeZone)

  return (
    <Flex
      className="time-range-control"
      gap={{ base: 3, md: 6 }}
      align={{ base: "stretch", md: "flex-end" }}
      flexWrap="wrap"
      minW={0}
      css={{ "--planner-colors-color-palette-focus-ring": "{colors.primary}" }}
    >
      <Box width="26rem" maxWidth="100%">
        <PopoverRoot
          open={open}
          onOpenChange={handleOpenChange}
          modal
          portalled={false}
          lazyMount
          unmountOnExit
          initialFocusEl={() => contentRef.current?.querySelector<HTMLElement>("input[type='radio']:checked") ?? null}
          positioning={{ placement: "bottom-start", sameWidth: true }}
          variant="hydrologiq"
        >
          <PopoverTrigger asChild>
            <Button
              variant="hydrologiq_secondary"
              width="100%"
              height="auto"
              minHeight="2.5em"
              py={1}
              gap={3}
              justifyContent="flex-start"
              textAlign="start"
              whiteSpace="normal"
              disabled={disabled}
              aria-label="Time range"
              aria-describedby={summaryId}
            >
              <FaRegCalendar aria-hidden="true" />
              <Text
                as="span"
                flex="1"
                minW={0}
                textAlign="start"
                fontSize="sm"
                fontVariantNumeric="tabular-nums"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {summary.title}
              </Text>
              <Text
                as="span"
                aria-hidden="true"
                px={2}
                py="1px"
                borderRadius="sm"
                bg="#e1f4f8"
                color="#1f6f85"
                fontSize="2xs"
                fontWeight={600}
                letterSpacing="0.04em"
                textTransform="uppercase"
              >
                {summary.badge}
              </Text>
              <FaChevronDown aria-hidden="true" />
              <VisuallyHidden id={summaryId}>{summary.description}</VisuallyHidden>
            </Button>
          </PopoverTrigger>

          <PopoverPositioner zIndex={1500}>
            {/* aria-label names the dialog directly: with lazy mounting the title is not registered for aria-labelledby. */}
            <PopoverContent
              ref={contentRef}
              aria-label="Time range"
              // The positioner is as wide as the trigger (sameWidth). The recipe sets `width` from --popover-size in a media
              // query that a `width` prop cannot override, so the popup fills the positioner through the variable.
              css={{ "--popover-size": "100%" }}
              maxWidth="calc(100vw - 2rem)"
              overflowY="auto"
              _open={{ _motionReduce: { animationName: "none" } }}
              _closed={{ _motionReduce: { animationName: "none" } }}
            >
              <PopoverBody>
                <VStack align="stretch" gap={2}>
                  <Box
                    data-tabs-bar
                    borderBottom="1px solid {colors.gray.400}"
                    css={{
                      "& [data-scope='segment-group'][data-part='root']": { width: "auto", pb: 0, backgroundColor: "transparent !important" },
                      "& [data-scope='segment-group'][data-part='item']": {
                        flex: "0 0 auto",
                        px: 4,
                        py: 2,
                        mb: "-1px",
                        fontWeight: 400,
                        // A transparent border shows the element's background, which would hide the bar's border under the tab.
                        backgroundColor: "transparent !important",
                        borderColor: "transparent",
                      },
                      "& [data-scope='segment-group'][data-part='item'][data-state='checked']": {
                        fontWeight: 600,
                        color: `${MODE_SWITCH_SELECTED} !important`,
                        borderColor: `${MODE_SWITCH_SELECTED} !important`,
                      },
                    }}
                  >
                    <SegmentedControl
                      variant="hydrologiq"
                      aria-label="Time range mode"
                      items={[
                        { value: "relative", label: "Relative" },
                        { value: "custom", label: "Custom" },
                      ]}
                      value={mode}
                      onValueChange={({ value }) => {
                        if (value === "relative" || value === "custom") handleModeChange(value)
                      }}
                    />
                  </Box>

                  {/* One element for both modes, with a minimum height that fits the taller (Custom) panel, so the popup does not resize when the mode changes. */}
                  <Box data-time-range-panel minHeight={PANEL_MIN_HEIGHT}>
                  {mode === "relative" ? (
                    <RadioGroup.Root
                      aria-label="Relative time range"
                      value={preset}
                      onValueChange={({ value }) => {
                        const next = TIMESCALE_PERIOD_PRESETS.find((item) => item.value === value)
                        if (next) handlePresetChange(next.value)
                      }}
                    >
                      <Flex align="stretch" gap={2} pt={1}>
                        {PRESET_COLUMNS.map((column, index) => (
                          <Fragment key={column.id}>
                            {index > 0 && <Box aria-hidden="true" width="1px" flexShrink={0} bg="gray.300" />}
                            <VStack align="stretch" gap={0} flex="1" minW={0} data-preset-column={column.id}>
                              {TIMESCALE_PERIOD_PRESETS.filter((item) => column.values.includes(item.value)).map((item) => (
                                <RadioGroup.Item
                                  key={item.value}
                                  value={item.value}
                                  minH="40px"
                                  px={3}
                                  py={2}
                                  borderRadius="md"
                                  cursor="pointer"
                                  justifyContent="space-between"
                                  _hover={{ bg: "blackAlpha.50" }}
                                  css={{
                                    "&[data-state='checked']": {
                                      fontWeight: 600,
                                      color: MODE_SWITCH_SELECTED,
                                      background: SELECTED_ROW_BACKGROUND,
                                    },
                                    "&[data-focus-visible]": {
                                      outline: "2px solid {colors.primary}",
                                      outlineOffset: "-2px",
                                    },
                                  }}
                                >
                                  <RadioGroup.ItemHiddenInput />
                                  <RadioGroup.ItemText>{item.label}</RadioGroup.ItemText>
                                  {preset === item.value && <FaCheck aria-hidden="true" size="0.75em" />}
                                </RadioGroup.Item>
                              ))}
                            </VStack>
                          </Fragment>
                        ))}
                      </Flex>
                    </RadioGroup.Root>
                  ) : (
                    <VStack align="stretch" gap={2}>
                      {(
                        [
                          { field: "from", label: "From", inputId: fromId, errorId: fromErrorId, ref: fromRef },
                          { field: "to", label: "To", inputId: toId, errorId: toErrorId, ref: toRef },
                        ] as const
                      ).map(({ field, label, inputId, errorId, ref }) => (
                        <Box key={field}>
                          <chakra.label htmlFor={inputId} fontSize="sm" fontWeight="medium" display="block" mb={1}>
                            {label}
                          </chakra.label>
                          <Input
                            ref={ref}
                            id={inputId}
                            type="datetime-local"
                            step={60}
                            min={limits?.min}
                            max={limits?.max}
                            value={draft[field]}
                            onChange={(event) => handleDraftChange(field, event.target.value)}
                            onBlur={handleBlur}
                            aria-invalid={errors[field] ? true : undefined}
                            aria-describedby={errors[field] ? errorId : undefined}
                          />
                          {/* Space is reserved so an error appearing on blur does not move Apply and Cancel mid-click. */}
                          <Box minHeight="1.25rem">
                            {errors[field] && (
                              <Text id={errorId} role="alert" fontSize="xs" color="red.600">
                                {errors[field]}
                              </Text>
                            )}
                          </Box>
                        </Box>
                      ))}

                      <Box minHeight="2.5rem">
                        {errors.form && (
                          <Text id={formErrorId} role="alert" fontSize="xs" color="red.600">
                            {errors.form}
                          </Text>
                        )}
                        <Text id={statusId} role="status" fontSize="xs" color="fg.muted">
                          {correction}
                        </Text>
                      </Box>
                    </VStack>
                  )}
                  </Box>

                  <HStack justifyContent="space-between" gap={3}>
                    <Text fontSize="xs" color="fg.muted">
                      Times are shown in {browserTimeZone}.
                    </Text>
                    <HStack gap={2} flexShrink={0}>
                      <Button size="sm" variant="hydrologiq_secondary" onClick={handleCancel}>
                        Cancel
                      </Button>
                      <Button size="sm" variant="hydrologiq_primary" onClick={handleApply} disabled={!hasChanges}>
                        Apply
                      </Button>
                    </HStack>
                  </HStack>
                </VStack>
              </PopoverBody>
            </PopoverContent>
          </PopoverPositioner>
        </PopoverRoot>
      </Box>

      {/* Outside the popup so it is announced after Apply has closed it. */}
      <VisuallyHidden role="status">{appliedCorrection}</VisuallyHidden>
    </Flex>
  )
}
